import {map, xor} from 'lodash';
import moment from 'moment';
import {Fragment, useCallback, useMemo} from 'react';
import {useTranslation} from 'react-i18next';

import {FiltersWrapper, FiltersWrapperChildrenProps, DateRangePicker, FiltersDropdown, Checkbox, Select} from 'components';
import {UserActionType} from 'enums';
import {TRANSLATION_KEYS} from 'locale';
import {User, UserAuditFilters} from 'types';
import {getParticipantName} from 'utils/transform';

const T_KEYS = TRANSLATION_KEYS.USER.ACCOUNT.USERS.USERS_AUDIT;

type Props = {
    users: User[],
    defaultFilters: UserAuditFilters,
    appliedFilters: UserAuditFilters,
    onFiltersChange: (filters: UserAuditFilters) => void,
}

export const UserAuditFiltersForm: React.FC<Props> = ({users, defaultFilters, appliedFilters, onFiltersChange: onFiltersChangeProp}) => {
    const {t} = useTranslation();
    const userSelectOptions = useMemo(() => map(users, (user) => ({id: user.email, name: getParticipantName(user)})), [users]);

    const renderActionCheckbox = useCallback(
        (labelTKey: string, action: UserActionType, filters: UserAuditFilters, onChange: (filters: Partial<UserAuditFilters>) => void) => {
            const checked = filters.actions.includes(action);
            const newFilters = {actions: xor(filters.actions, [action])};
            return (
                <Checkbox
                    size='small'
                    name={`checkbox-${action}`}
                    label={labelTKey}
                    checked={checked}
                    onChange={() => onChange(newFilters)}
                />
            );
        }, []);

    return (
        <FiltersWrapper
            defaultFilters={defaultFilters}
            filters={appliedFilters}
            onSubmit={onFiltersChangeProp}
        >
            {({filters, onFiltersChange, ...rest}: FiltersWrapperChildrenProps<UserAuditFilters>) => (
                <div className='tw-my-4 tw-flex tw-items-center tw-flex-wrap'>
                    <DateRangePicker
                        from={moment(filters.createdAfterDate)}
                        to={moment(filters.createdBeforeDate)}
                        className='tw-mr-3 -sm:tw-my-2'
                        moveDayClassName='-sm:tw-hidden'
                        onChange={(createdAfterDate, createdBeforeDate, options) =>
                            onFiltersChange({createdAfterDate, createdBeforeDate}, {apply: options?.method === 'arrow'})}
                    />
                    <FiltersDropdown className='tw-mr-2 -sm:tw-my-2' {...rest}>
                        <Fragment>
                            <Select
                                placeholder={t(T_KEYS.TABLE.COLUMNS.AUTHOR)}
                                options={userSelectOptions}
                                noneOption
                                valueAs='id'
                                value={filters.authorEmail}
                                onChange={(id) => onFiltersChange({authorEmail: id})}
                            />
                            <Select
                                placeholder={t(T_KEYS.TABLE.COLUMNS.USER)}
                                options={userSelectOptions}
                                noneOption
                                valueAs='id'
                                value={filters.userEmail}
                                onChange={(id) => onFiltersChange({userEmail: id})}
                            />
                            <div className='tw-grid tw-gap-3 tw-px-2 tw-my-1'>
                                <span className='tw-ts-h5-bold tw-my-2'>{t(T_KEYS.ACTIONS.TITLE)}</span>
                                {renderActionCheckbox(t(T_KEYS.ACTIONS.CREATE), UserActionType.Create, filters, onFiltersChange)}
                                {renderActionCheckbox(t(T_KEYS.ACTIONS.UPDATE), UserActionType.Update, filters, onFiltersChange)}
                                {renderActionCheckbox(t(T_KEYS.ACTIONS.DELETE), UserActionType.Delete, filters, onFiltersChange)}
                            </div>
                        </Fragment>
                    </FiltersDropdown>
                </div>
            )}
        </FiltersWrapper>
    );
};